import {
    canCreateCharacter,
    findCharacterAppearanceError,
    findCharacterNameError,
    isBodyPartCompatible,
    CREATION_ATTRIBUTE_POINTS,
    DEFAULT_STARTING_ARMOR,
    DEFAULT_STARTING_WEAPON,
    findStartingEquipmentOption,
    countAttributePointsSpent,
    MINIMUM_CHARACTER_ATTRIBUTES,
    STARTING_ARMOR_OPTIONS,
    STARTING_WEAPON_OPTIONS,
    type BodyPart,
    type BodyPartModifier,
    type CharacterAttributes,
    type CharacterModelSelection,
    type StartingEquipmentOption,
} from '@sorrem/shared';
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppNavigate } from '@sorrem-client/ui/hooks';
import { resolvePostCreationPath } from '@sorrem-client/ui/shared/post-login-path';
import { useCurrentPlayerCharacter } from '@sorrem-client/ui/hooks/use-current-player-character';
import { DEFAULT_HAIR_COLOR_ID, HAIR_COLOR_SWATCHES, type HairColorSwatch } from '@sorrem-client/ui/features/Character/hair-color-palette';

export type Gender = 'male' | 'female';

const GENDER_BASE_MODELS: Record<Gender, string> = {
    male: 'quaternius_male',
    female: 'quaternius_female',
};

const GENDER_DEFAULT_HAIR: Record<Gender, string> = {
    male: 'hair-buzzed',
    female: 'hair-ponytail-2',
};

const GENDER_DEFAULT_EYEBROWS: Record<Gender, string> = {
    male: 'eyebrows-regular',
    female: 'eyebrows-female',
};

export const HAIR_SLOT = 'hair';
export const EYEBROWS_SLOT = 'eyebrows';
export const FACIAL_HAIR_SLOT = 'facial-hair';

/** Every attribute before a creation point is placed: the player spends all of `CREATION_ATTRIBUTE_POINTS`. */
function freshAttributes(): CharacterAttributes {
    return { ...MINIMUM_CHARACTER_ATTRIBUTES };
}

export interface CharacterCreationDraft {
    name: string;
    setName: (name: string) => void;
    gender: Gender;
    setGender: (gender: Gender) => void;
    attributes: CharacterAttributes;
    onChangeAttribute: (attribute: keyof CharacterAttributes, nextValue: number) => void;
    remainingPoints: number;
    hasAllocatedAllPoints: boolean;
    hairstyleId: string;
    setHairstyleId: (id: string) => void;
    eyebrowsId: string;
    setEyebrowsId: (id: string) => void;
    facialHairId: string;
    setFacialHairId: (id: string) => void;
    hairColorId: string;
    setHairColorId: (id: string) => void;
    /** The hair colors to pick from. */
    hairColorSwatches: readonly HairColorSwatch[];
    hasFacialHairOptions: boolean;
    /** The picked starting armor, worn as the outfit: an `itemDefinitionId` from `STARTING_ARMOR_OPTIONS`. */
    armorItemId: string;
    setArmorItemId: (id: string) => void;
    /** The starting armor to pick from. */
    armorOptions: readonly StartingEquipmentOption[];
    /** The picked starting weapon: an `itemDefinitionId` from `STARTING_WEAPON_OPTIONS`. */
    weaponItemId: string;
    setWeaponItemId: (id: string) => void;
    /** The starting weapons to pick from. */
    weaponOptions: readonly StartingEquipmentOption[];
    /** The base humanoid model (`quaternius_male`/`quaternius_female`) the draft character previews as - what the persistent viewport loads while creating. */
    selectedBaseModel: string;
    equippedItemIds: string[];
    outfitModifiers: Partial<Record<string, BodyPartModifier>>;
    hasExistingCharacters: boolean;
    canCreateCharacter: boolean;
    /** Everything Create needs: room for another character, a name, every point placed, and not already saving. */
    canSubmit: boolean;
    isNameValid: boolean;
    isSaving: boolean;
    createError: string | null;
    onCancel: () => void;
    onCreate: () => void;
}

/**
 * All character-creation state, lifted out of the creation panels
 * themselves (`CreationIdentityPanel`/`CreationAppearancePanel`/
 * `CreationActionsPanel`) so `CharacterHome.tsx` can also read
 * `selectedBaseModel`/`equippedItemIds`/`outfitModifiers` for the shared
 * persistent viewport while creating - one draft, read by both the panels
 * and the background character preview, never two independent copies.
 *
 * `resetKey` should change (e.g. `isCreationViewShown` flipping false -> true)
 * whenever creation should start fresh - see the effect below. Without it
 * the draft would survive across a cancel/re-open since this hook is called
 * from the always-mounted `CharacterHome`, not a component that remounts
 * per visit the way the old standalone `/character/create` route did.
 */
export function useCharacterCreationDraft(bodyParts: readonly BodyPart[], resetKey: boolean): CharacterCreationDraft {
    const navigate = useAppNavigate();
    const { pathname } = useLocation();
    const {
        accountProfile,
        characters,
        createCharacter,
    } = useCurrentPlayerCharacter();

    // The name the player typed; null until they type, so the default below shows instead.
    const [chosenName, setName] = useState<string | null>(null);
    const [gender, setGender] = useState<Gender>('male');
    // The player's picks; what shows is derived below, so a pick made for the other body never shows.
    const [chosenHairstyleId, setHairstyleId] = useState<string>(GENDER_DEFAULT_HAIR.male);
    const [chosenEyebrowsId, setEyebrowsId] = useState<string>(GENDER_DEFAULT_EYEBROWS.male);
    const [chosenFacialHairId, setFacialHairId] = useState<string>('');
    const [hairColorId, setHairColorId] = useState<string>(DEFAULT_HAIR_COLOR_ID);
    const [armorItemId, setArmorItemId] = useState<string>(DEFAULT_STARTING_ARMOR.itemDefinitionId);
    const [weaponItemId, setWeaponItemId] = useState<string>(DEFAULT_STARTING_WEAPON.itemDefinitionId);
    const [isSaving, setIsSaving] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const [attributes, setAttributes] = useState<CharacterAttributes>(freshAttributes);

    // Fresh draft every time creation is (re-)entered, since this hook lives
    // in an always-mounted parent rather than remounting per visit. Reset
    // while rendering, so the old draft never shows for a frame.
    const [lastResetKey, setLastResetKey] = useState(resetKey);
    if (resetKey !== lastResetKey) {
        setLastResetKey(resetKey);
        if (resetKey) {
            setName(null);
            setGender('male');
            setHairstyleId(GENDER_DEFAULT_HAIR.male);
            setEyebrowsId(GENDER_DEFAULT_EYEBROWS.male);
            setFacialHairId('');
            setHairColorId(DEFAULT_HAIR_COLOR_ID);
            setArmorItemId(DEFAULT_STARTING_ARMOR.itemDefinitionId);
            setWeaponItemId(DEFAULT_STARTING_WEAPON.itemDefinitionId);
            setCreateError(null);
            setAttributes(freshAttributes());
        }
    }

    const hasExistingCharacters = characters.length > 0;
    // An account's first character starts with its Discord name; every later one starts unnamed.
    const name = chosenName ?? (hasExistingCharacters ? '' : accountProfile?.account.displayName ?? '');
    const isUnderCharacterLimit = accountProfile !== null
        && canCreateCharacter(accountProfile.accountTypeConfig.maxCharacters, characters.length);

    const remainingPoints = useMemo(
        () => CREATION_ATTRIBUTE_POINTS - countAttributePointsSpent(attributes),
        [attributes],
    );
    const isNameValid = findCharacterNameError(name) === undefined;
    const hasAllocatedAllPoints = remainingPoints === 0;

    const selectedBaseModel = GENDER_BASE_MODELS[gender];

    // A hairstyle/eyebrows/facial-hair pick made under one gender may not
    // exist for the other (most styles are gender-locked in the catalog):
    // that gender's default shows instead (or, for the optional facial hair,
    // none), and the pick comes back if the player switches back.
    const fits = (id: string, slot: string): boolean => bodyParts.some((part) => part.id === id && isBodyPartCompatible(part, slot, selectedBaseModel));
    const hairstyleId = fits(chosenHairstyleId, HAIR_SLOT) ? chosenHairstyleId : GENDER_DEFAULT_HAIR[gender];
    const eyebrowsId = fits(chosenEyebrowsId, EYEBROWS_SLOT) ? chosenEyebrowsId : GENDER_DEFAULT_EYEBROWS[gender];
    const facialHairId = chosenFacialHairId !== '' && fits(chosenFacialHairId, FACIAL_HAIR_SLOT) ? chosenFacialHairId : '';

    const outfit = useMemo<Record<string, string>>(() => {
        // Clothing comes from the starting armor (see `equippedItemIds`), not this cosmetic map.
        const next: Record<string, string> = {
            [HAIR_SLOT]: hairstyleId,
            [EYEBROWS_SLOT]: eyebrowsId,
        };
        if (facialHairId) {
            next[FACIAL_HAIR_SLOT] = facialHairId;
        }
        return next;
    }, [hairstyleId, eyebrowsId, facialHairId]);

    // Hair color applies uniformly to every hair-like slot (head hair,
    // eyebrows, facial hair) rather than being a separate pick per slot -
    // harmless to set on a slot that isn't currently equipped (facial hair).
    const outfitModifiers = useMemo<Partial<Record<string, BodyPartModifier>>>(() => {
        const selectedColor = HAIR_COLOR_SWATCHES.find((swatch) => swatch.id === hairColorId);
        if (!selectedColor) {
            return {};
        }
        const modifier: BodyPartModifier = { primaryColor: selectedColor.hex };
        return { [HAIR_SLOT]: modifier, [EYEBROWS_SLOT]: modifier, [FACIAL_HAIR_SLOT]: modifier };
    }, [hairColorId]);

    // What the preview wears: the starting armor (which the renderer swaps for its outfit parts on
    // this body) and weapon by model id, as the character will once created, then the cosmetic parts.
    // A pick that is not a starting option shows nothing in its place; the server refuses it.
    const equippedItemIds = useMemo(() => {
        const armor = findStartingEquipmentOption(STARTING_ARMOR_OPTIONS, armorItemId);
        const weapon = findStartingEquipmentOption(STARTING_WEAPON_OPTIONS, weaponItemId);
        return [armor?.modelId, weapon?.modelId, ...Object.values(outfit)].filter((id): id is string => Boolean(id));
    }, [armorItemId, weaponItemId, outfit]);

    const hasFacialHairOptions = useMemo(
        () => bodyParts.some((part) => isBodyPartCompatible(part, FACIAL_HAIR_SLOT, selectedBaseModel)),
        [bodyParts, selectedBaseModel],
    );

    const onCancel = useCallback((): void => {
        if (!hasExistingCharacters) {
            return;
        }

        navigate('/character/overview');
    }, [hasExistingCharacters, navigate]);

    const onChangeAttribute = useCallback((attribute: keyof CharacterAttributes, nextValue: number): void => {
        setAttributes((previous) => ({
            ...previous,
            [attribute]: nextValue,
        }));
    }, []);

    const characterModel = useMemo<CharacterModelSelection>(
        () => ({ baseModelId: selectedBaseModel, outfit, outfitModifiers }),
        [outfit, outfitModifiers, selectedBaseModel],
    );
    // The server's own check on the look (#331): no part it would refuse, e.g. before the catalog loads.
    const isAppearanceValid = findCharacterAppearanceError(characterModel, bodyParts) === undefined;

    // One guard for the button and the create itself (#330).
    const canSubmit = isUnderCharacterLimit && isNameValid && hasAllocatedAllPoints && eyebrowsId !== '' && isAppearanceValid && !isSaving;

    const onCreate = useCallback((): void => {
        if (!canSubmit) {
            return;
        }

        setCreateError(null);
        setIsSaving(true);

        void createCharacter({ name, characterModel, attributes, startingArmorItemId: armorItemId, startingWeaponItemId: weaponItemId })
            .then((result) => {
                if ('error' in result) {
                    setCreateError(result.error);
                    return;
                }

                // On to the invite the player was following, if any (#216).
                navigate(resolvePostCreationPath(pathname), { replace: true });
            })
            .finally(() => {
                setIsSaving(false);
            });
    }, [armorItemId, attributes, canSubmit, characterModel, createCharacter, name, navigate, pathname, weaponItemId]);

    return {
        name,
        setName,
        gender,
        setGender,
        attributes,
        onChangeAttribute,
        remainingPoints,
        hasAllocatedAllPoints,
        hairstyleId,
        setHairstyleId,
        eyebrowsId,
        setEyebrowsId,
        facialHairId,
        setFacialHairId,
        hairColorId,
        hairColorSwatches: HAIR_COLOR_SWATCHES,
        setHairColorId,
        hasFacialHairOptions,
        armorItemId,
        armorOptions: STARTING_ARMOR_OPTIONS,
        setArmorItemId,
        weaponItemId,
        weaponOptions: STARTING_WEAPON_OPTIONS,
        setWeaponItemId,
        selectedBaseModel,
        equippedItemIds,
        outfitModifiers,
        hasExistingCharacters,
        canCreateCharacter: isUnderCharacterLimit,
        canSubmit,
        isNameValid,
        isSaving,
        createError,
        onCancel,
        onCreate,
    };
}
